import { action, observable, reaction } from 'mobx';
import Alert from 'react-s-alert';
import { AppManager, Variables, Input } from 'core';

import { clientStore } from 'apps/client/app';
import { SharedFileConnManager } from 'apps/share/connManager';
import {
  ProfileSharedFilesQuery,
  ProfileSharedWithFilesQuery,
  InitiativeSharedFilesQuery,
  DeleteSharedFileMutation,
} from 'apps/share/gql';

export default class FilesAppManager extends AppManager {
  static registryName = 'FilesAppManager';

  @observable sharedFilesConnection = null;

  lastQueryParameter = null;

  constructor(parent, init = {}, options) {
    super(parent, init, options);

    this.variables = new Variables({
      username: () =>
        clientStore.activeSpace
          ? clientStore.activeSpace?.path
          : clientStore.signedInProfile?.user.username,
      slug: () => clientStore.activeSpace?.path,
    });

    this.disposers.push(
      reaction(
        () => ({
          activeSpace: clientStore.activeSpace,
          routeName: clientStore.routerStore.route.name,
        }),
        ({ activeSpace, routeName }) => {
          if (!activeSpace || activeSpace.spaceType === 'PROFILE') {
            // Next "if" is just an optimization so that the first query happens immediately (see { fireImmediately: true })
            // which will use the username from signedInProfile, but if the activeSpace that is resolved is the space
            // for that profile we don't want to requery.
            // There's a boolean so that this skip only happens once, which is reasonable but has no real usecase at time of writing.
            // We could potentially be getting into an activeSpace different than the one in signedInProfile,
            // hence the this.lastQueryParameter check.
            if (
              activeSpace &&
              clientStore.isTheCurrentActiveSpaceTheFirstOneUsed &&
              this.lastQueryParameter === this.variables.username
            ) {
              return;
            }

            const isSharedWithFiles = routeName === 'workspace.sharedWithFiles';
            this.setupNewConnManagerWithQuery(
              isSharedWithFiles
                ? ProfileSharedWithFilesQuery
                : ProfileSharedFilesQuery,
            );

            this.lastQueryParameter = this.variables.username;
            this.FilesQuery();
          } else if (activeSpace.spaceType === 'INITIATIVE') {
            this.setupNewConnManagerWithQuery(InitiativeSharedFilesQuery);

            this.lastQueryParameter = this.variables.slug;
            this.FilesQuery();
          }
        },
        { fireImmediately: true },
      ),
    );
  }

  setupNewConnManagerWithQuery = (Query = ProfileSharedFilesQuery) => {
    this.lastQueryUsed = Query;
    this.sharedFilesConnection?.dispose();
    this.sharedFilesConnection = new SharedFileConnManager(this, {
      sortBy: 'recent',
      initialBatchSize: 6,
      loadMoreQuery: Query,
    });
  };

  @action FilesQuery = () => {
    this.sharedFilesConnection.loadMore();
  };

  @action DeleteSharedFile = sharedFile => {
    const input = new Input({
      id: sharedFile.id,
    });

    const mutation = DeleteSharedFileMutation.New(input, {
      onSuccess: this.onDeleteSharedFileSuccess,
      onSysErr: this.onDeleteSharedFileError,
      onUserErr: this.onDeleteSharedFileError,
    });
    this.mutations.push(mutation);
  };

  onDeleteSharedFileError = () => {
    Alert.error(
      'There was a problem deleting your shared file. Please, try again later',
    );
  };

  onDeleteSharedFileSuccess = () => {
    Alert.success('Your Shared file has been deleted!');
    clientStore.UpdateProfileOrTeamWorkspace();
    this.refresh();
  };

  @action refresh = () => {
    this.setupNewConnManagerWithQuery(this.lastQueryUsed);
    this.FilesQuery();
  };
}
